<template>
  <div class="report-sections">
    <!-- ========== 收入 ========== -->
    <section class="report-card">
      <div class="report-card__head">
        <h2 class="report-card__title">收入</h2>
      </div>

      <report-group
        title="新生收入"
        :count="report.new_count"
        :amount="report.new_income"
        tone="income"
      >
        <el-table :data="list.newCards" empty-text="当天没有相关记录" border fit>
          <v-column-time fixed="left" align="center"/>
          <v-column-student link-page-id="student_id" align="center" width="140" label="办卡学员"/>
          <el-table-column label="课卡" align="center" min-width="180">
            <template v-slot="{ row }">
              <div class="cell-main">{{ row.card_name }}</div>
              <div class="cell-sub">{{ row.card_type_name }}</div>
            </template>
          </el-table-column>
          <el-table-column label="实际付款" align="center" width="110">
            <template v-slot="{ row }">
              <span>{{ row.actual_payment | formatCent }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收款方式" align="center" min-width="150">
            <template v-slot="{ row }">
              <div class="cell-main">{{ findPaymentModeName(row.card_payment_mode_id) }}</div>
              <div v-if="row.payment_remark" class="cell-sub">{{ row.payment_remark }}</div>
            </template>
          </el-table-column>
          <v-column-admin width="132" align="center"/>
        </el-table>
        <el-pagination
          v-show="report.new_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.new_count"
          @current-change="page => $emit('page', { key: 'new', page })"
        />
      </report-group>

      <report-group
        title="老生收入"
        :count="report.old_count"
        :amount="report.old_income"
        tone="income"
      >
        <el-table :data="list.oldCards" empty-text="当天没有相关记录" border fit>
          <v-column-time fixed="left" align="center"/>
          <v-column-student link-page-id="student_id" align="center" width="140" label="办卡学员"/>
          <el-table-column label="课卡" align="center" min-width="180">
            <template v-slot="{ row }">
              <div class="cell-main">{{ row.card_name }}</div>
              <div class="cell-sub">{{ row.card_type_name }}</div>
            </template>
          </el-table-column>
          <el-table-column label="实际付款" align="center" width="110">
            <template v-slot="{ row }">
              <span>{{ row.actual_payment | formatCent }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收款方式" align="center" min-width="150">
            <template v-slot="{ row }">
              <div class="cell-main">{{ findPaymentModeName(row.card_payment_mode_id) }}</div>
              <div v-if="row.payment_remark" class="cell-sub">{{ row.payment_remark }}</div>
            </template>
          </el-table-column>
          <v-column-admin width="132" align="center"/>
        </el-table>
        <el-pagination
          v-show="report.old_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.old_count"
          @current-change="page => $emit('page', { key: 'old', page })"
        />
      </report-group>

      <report-group
        title="试课收入"
        :count="report.trial_count"
        :amount="report.trial_income"
        tone="income"
      >
        <el-table :data="list.trialPayments" empty-text="当天没有相关记录" border fit>
          <v-column-time fixed="left" align="center"/>
          <v-column-admin align="center" label="添加者"/>
          <v-column-student link-page-id="student_id" align="center" label="试课学员"/>
          <el-table-column prop="course_name" label="课程名称" align="center"/>
          <el-table-column label="实际付款" align="center">
            <template v-slot="{ row }">{{ row.actual_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column label="收款方式" align="center">
            <template v-slot="{ row }">{{ findPaymentModeName(row.card_payment_mode_id) }}</template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-show="report.trial_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.trial_count"
          @current-change="page => $emit('page', { key: 'trial', page })"
        />
      </report-group>

      <report-group
        title="课次券收入"
        :count="report.card_ticket_income_count"
        :amount="report.card_ticket_income"
        tone="income"
        :hint="cardTicketIncomeHint"
      >
        <el-table :data="list.cardTicketIncomes" empty-text="当天没有相关记录" border fit>
          <v-column-time value="计入时间" fixed="left" align="center"/>
          <el-table-column label="来源" align="center" width="110">
            <template v-slot="{ row }">
              <el-tag size="small" :type="row.is_app ? 'warning' : 'info'">{{ row.source_name }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="经办人" align="center" width="150">
            <template v-slot="{ row }">
              <span v-if="row.is_app" class="primary-color">系统自动计入</span>
              <span v-else>{{ row.administrator_nickname || '-' }}</span>
            </template>
          </el-table-column>
          <v-column-student link-page-id="student_id" align="center" label="办理学员"/>
          <el-table-column prop="card_ticket_type_name" label="课次券名称" align="center"/>
          <el-table-column label="实际付款" align="center">
            <template v-slot="{ row }">
              <span>{{ row.actual_payment | formatCent }}</span>
              <span v-if="row.is_refund" class="danger-color">（已退款）</span>
            </template>
          </el-table-column>
          <el-table-column prop="payment_remark" label="付款备注" align="center"/>
          <el-table-column label="收款方式" align="center">
            <template v-slot="{ row }">{{ findPaymentModeName(row.card_payment_mode_id) }}</template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-show="report.card_ticket_income_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.card_ticket_income_count"
          @current-change="page => $emit('page', { key: 'cardTicket', page })"
        />
      </report-group>

      <report-group
        title="代金券收入"
        :count="report.voucher_income_count"
        :amount="report.voucher_income"
        tone="income"
        :hint="voucherIncomeHint"
      >
        <el-table :data="list.voucherIncomes" empty-text="当天没有相关记录" border fit>
          <v-column-time value="计入时间" fixed="left" align="center"/>
          <el-table-column label="来源" align="center" width="110">
            <template v-slot>
              <el-tag size="small" type="warning">小程序购买</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="经办人" align="center" width="150">
            <template v-slot>
              <span class="primary-color">系统自动计入</span>
            </template>
          </el-table-column>
          <v-column-student link-page-id="student_id" align="center" label="购买学员"/>
          <el-table-column prop="voucher_type_name" label="代金券名称" align="center"/>
          <el-table-column label="售价（元）" align="center">
            <template v-slot="{ row }">
              <span>{{ row.sale_price_in_cents | formatCent }}</span>
              <span v-if="row.is_refund" class="danger-color">（已退款）</span>
            </template>
          </el-table-column>
          <el-table-column prop="store_source_name" label="归属依据" align="center"/>
        </el-table>
        <el-pagination
          v-show="report.voucher_income_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.voucher_income_count"
          @current-change="page => $emit('page', { key: 'voucher', page })"
        />
      </report-group>

      <report-group
        title="修改课卡补差价收入"
        :count="report.update_income_count"
        :amount="report.update_income"
        tone="income"
      >
        <el-table :data="list.updateIncomes" empty-text="当天没有相关记录" border fit>
          <v-column-time value="审核时间" fixed="left" align="center"/>
          <v-column-admin align="center" label="审核人"/>
          <v-column-avatar
            align="center"
            label="申请人"
            avatarKey="apply_administrator_avatar"
            nicknameKey="apply_administrator_nickname"
          />
          <v-column-student link-page-id="student_id" align="center" label="课卡所属学员"/>
          <el-table-column label="补差价收入" align="center">
            <template v-slot="{ row }">{{ row.update_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column label="补差价支付方式" align="center">
            <template v-slot="{ row }">{{ findPaymentModeName(row.update_payment_mode) }}</template>
          </el-table-column>
        </el-table>
      </report-group>

      <report-group
        title="小程序预约收入"
        :count="report.reservation_income_count"
        :amount="report.reservation_income"
        tone="income"
      >
        <el-table
          :data="list.reservationIncomes"
          empty-text="当天没有相关记录"
          border
          fit
          :row-class-name="tableRowClassName"
        >
          <v-column-time field="success_time_int" value="支付时间" fixed="left" align="center"/>
          <el-table-column label="订单总金额" align="center">
            <template v-slot="{ row }">
              <span>{{ row.total | formatCent }}</span>
              <span v-if="row.is_refund" class="danger-color">（已申请退款）</span>
            </template>
          </el-table-column>
          <v-column-student link-page-id="student_id" align="center" label="下单学员"/>
          <el-table-column label="微信支付订单号" align="center" prop="transaction_id"/>
          <el-table-column label="预约课程/专栏" align="center">
            <template v-slot="{ row }">
              <router-link
                v-if="row.course_id"
                class="primary-color"
                :to="RouteConfig.courseManage.to(row.course_id)"
              >{{ row.course_name }}</router-link>
              <router-link
                v-else
                class="primary-color"
                :to="RouteConfig.courseColumnManage.to(row.course_column_id)"
              >{{ row.course_column_name }}</router-link>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-show="report.reservation_income_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.reservation_income_count"
          @current-change="page => $emit('page', { key: 'reservationIncome', page })"
        />
      </report-group>

      <slot name="income-extra"/>
    </section>

    <!-- ========== 退款类支出（不含备用金支出，备用金单独成卡） ========== -->
    <section class="report-card">
      <div class="report-card__head">
        <h2 class="report-card__title">退款类支出</h2>
      </div>

      <report-group
        title="退卡支出"
        :count="report.refund_count"
        :amount="report.refund_pay"
        tone="pay"
      >
        <el-table :data="list.refunds" empty-text="当天没有相关记录" border fit>
          <v-column-time value="退款时间" fixed="left" align="center"/>
          <v-column-student label="退款学员" link-page-id="student_id" align="center" width="140"/>
          <el-table-column label="课卡" align="center" min-width="180">
            <template v-slot="{ row }">
              <div class="cell-main">{{ row.card_name }}</div>
              <div class="cell-sub">{{ row.card_type_name }}</div>
            </template>
          </el-table-column>
          <el-table-column label="实际收款" align="center" width="110">
            <template v-slot="{ row }">{{ row.actual_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column label="收款方式" align="center" width="160">
            <template v-slot="{ row }">
              <div v-if="row.actual_payment" class="cell-main">{{ findPaymentModeName(row.card_payment_mode_id) }}</div>
              <div v-else class="cell-main info-color">未付款</div>
              <div v-if="row.payment_remark" class="cell-sub">{{ row.payment_remark }}</div>
            </template>
          </el-table-column>
          <el-table-column label="退款金额" align="center">
            <template v-slot="{ row }">{{ row.del_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column prop="del_remark" label="退款原因" align="center"/>
          <el-table-column label="退款原因截图" align="center">
            <template v-slot="{ row }">
              <el-image
                v-if="row.del_cover"
                :src="row.del_cover"
                style="width: 60px;height: 60px;"
                fit="cover"
              />
            </template>
          </el-table-column>
        </el-table>
        <div class="group-extra">
          <span>因录入错误导致的退卡</span>
          <b>{{ report.refund_pay_input_error | formatCent }}</b>
        </div>
      </report-group>

      <report-group
        title="券退款支出"
        :count="report.ticket_refund_count"
        :amount="report.ticket_refund_pay"
        tone="pay"
        badge="需转总部"
      >
        <h4 class="sub-title">课次券退款</h4>
        <el-table :data="list.cardTicketRefunds" empty-text="当天没有相关记录" border fit>
          <v-column-time field="add_time" value="退款时间" fixed="left" align="center"/>
          <v-column-student link-page-id="student_id" align="center" label="退款学员"/>
          <el-table-column prop="card_ticket_type_name" label="课次券名称" align="center"/>
          <el-table-column label="实付金额" align="center">
            <template v-slot="{ row }">{{ row.actual_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column label="退款金额" align="center">
            <template v-slot="{ row }">
              <span>{{ row.del_payment | formatCent }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="reason" label="退款原因" align="center"/>
          <el-table-column prop="transaction_id" label="微信支付订单号" align="center"/>
        </el-table>
        <el-pagination
          v-show="refundTotals.cardTicketCount > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="refundTotals.cardTicketCount"
          @current-change="page => $emit('page', { key: 'cardTicketRefund', page })"
        />

        <h4 class="sub-title">代金券退款</h4>
        <el-table :data="list.voucherRefunds" empty-text="当天没有相关记录" border fit>
          <v-column-time field="add_time" value="退款时间" fixed="left" align="center"/>
          <v-column-student link-page-id="student_id" align="center" label="退款学员"/>
          <el-table-column prop="voucher_type_name" label="代金券名称" align="center"/>
          <el-table-column label="售价（元）" align="center">
            <template v-slot="{ row }">{{ row.sale_price_in_cents | formatCent }}</template>
          </el-table-column>
          <el-table-column label="退款金额" align="center">
            <template v-slot="{ row }">
              <span>{{ row.del_payment | formatCent }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="reason" label="退款原因" align="center"/>
          <el-table-column prop="transaction_id" label="微信支付订单号" align="center"/>
        </el-table>
        <el-pagination
          v-show="refundTotals.voucherCount > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="refundTotals.voucherCount"
          @current-change="page => $emit('page', { key: 'voucherRefund', page })"
        />
      </report-group>

      <report-group
        title="小程序预约退款"
        :count="report.reservation_pay_count"
        :amount="report.reservation_pay"
        tone="pay"
        badge="需转总部"
      >
        <el-table :data="list.reservationPays" empty-text="当天没有相关记录" border fit>
          <v-column-time value="退款时间" field="success_time_int" fixed="left" align="center"/>
          <v-column-student label="退款学员" link-page-id="student_id" align="center"/>
          <el-table-column label="微信支付订单号" align="center" prop="transaction_id"/>
          <el-table-column label="订单总金额" align="center">
            <template v-slot="{ row }">{{ row.total | formatCent }}</template>
          </el-table-column>
          <el-table-column label="退款金额" align="center">
            <template v-slot="{ row }">
              <span>{{ row.payer_refund | formatCent }}</span>
            </template>
          </el-table-column>
          <el-table-column label="退款课程/专栏" align="center">
            <template v-slot="{ row }">
              <router-link
                v-if="row.course_id"
                class="primary-color"
                :to="RouteConfig.courseManage.to(row.course_id)"
              >{{ row.course_name }}</router-link>
              <router-link
                v-else
                class="primary-color"
                :to="RouteConfig.courseColumnManage.to(row.course_column_id)"
              >{{ row.course_column_name }}</router-link>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-show="report.reservation_pay_count > pageSize"
          class="group-pagination"
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="report.reservation_pay_count"
          @current-change="page => $emit('page', { key: 'reservationPay', page })"
        />
      </report-group>

      <report-group
        title="修改课卡补差价支出"
        :count="report.update_pay_count"
        :amount="report.update_pay"
        tone="pay"
      >
        <el-table :data="list.updatePays" empty-text="当天没有相关记录" border fit>
          <v-column-time value="审核时间" fixed="left" align="center"/>
          <v-column-admin align="center" label="审核人"/>
          <v-column-avatar
            align="center"
            label="申请人"
            avatarKey="apply_administrator_avatar"
            nicknameKey="apply_administrator_nickname"
          />
          <v-column-student link-page-id="student_id" align="center" label="课卡所属学员"/>
          <el-table-column label="补差价支出" align="center">
            <template v-slot="{ row }">{{ -row.update_payment | formatCent }}</template>
          </el-table-column>
          <el-table-column label="补差价支付方式" align="center">
            <template v-slot="{ row }">{{ findPaymentModeName(row.update_payment_mode) }}</template>
          </el-table-column>
        </el-table>
      </report-group>
    </section>

    <slot name="after-expense"/>

    <!-- ========== 收款方式 ========== -->
    <section class="report-card">
      <div class="report-card__head">
        <h2 class="report-card__title">收款方式</h2>
      </div>
      <div class="report-card__body">
        <div class="pay-grid">
          <div v-for="item in paymentRows" :key="item.label" class="pay-item">
            <span>{{ item.label }}</span>
            <b :class="{ 'is-zero': !item.value }">{{ item.value | formatCent }}</b>
          </div>
          <div class="pay-item is-total">
            <span>总收入</span>
            <b>{{ report.all_income | formatCent }}</b>
          </div>
          <div v-if="isAdministrator" class="pay-item">
            <span>产生课消</span>
            <b :class="{ 'is-zero': !report.course_consumption }">{{ report.course_consumption | formatCent }}</b>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== 大课人数 ========== -->
    <section class="report-card">
      <div class="report-card__head">
        <h2 class="report-card__title">大课人数</h2>
      </div>
      <div class="report-card__body">
        <div class="pay-grid">
          <div class="pay-item">
            <span>总课时数</span>
            <b :class="{ 'is-zero': !report.group_course_count }">{{ report.group_course_count | formatCent }} 小时</b>
          </div>
          <div class="pay-item">
            <span>课时总人次</span>
            <b :class="{ 'is-zero': !report.total_attendance }">{{ report.total_attendance | formatCent }} 人</b>
          </div>
          <div class="pay-item">
            <span>每课时平均人数</span>
            <b :class="{ 'is-zero': !report.average_attendance }">{{ report.average_attendance | formatCent }} 人</b>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import RouteConfig from '@/router/config';
import { findPaymentModeName } from '@/enums/paymentModes';
import ReportGroup from './ReportGroup.vue';

export default {
  name: 'DailyReportSections',
  components: { ReportGroup },
  props: {
    report: { type: Object, required: true },
    list: { type: Object, required: true },
    refundTotals: {
      type: Object,
      default: () => ({
        cardTicketCount: 0, cardTicketTotal: 0, voucherCount: 0, voucherTotal: 0,
      }),
    },
    isAdministrator: { type: Boolean, default: false },
  },
  data: () => ({
    RouteConfig,
    pageSize: 10,
    // 口径说明改走 tooltip，不再每个分区挂一条常驻提示
    cardTicketIncomeHint: '小程序购买且已归属门店的课次券按购买日计入；未归属的按首次签到/爽约课程日计入；门店办理的按办理日计入。',
    voucherIncomeHint: '小程序购买且已归属门店的代金券按购买日计入；未归属的按用券办卡日计入。',
  }),
  computed: {
    // 收款方式各渠道；金额为 0 时行内数字弱化，避免满屏都是强调色
    paymentRows() {
      const { report } = this;
      return [
        { label: '门店微信账号', value: report.we_chat_income },
        { label: '门店支付宝账号', value: report.alipay_income },
        { label: '门店富掌柜账号', value: report.fu_zhang_gui_income },
        { label: '门店美团账号', value: report.meituan_income },
        { label: '门店银行账号', value: report.card_income },
        { label: '现金', value: report.cash_income },
        { label: '总部有赞账号', value: report.you_zan_income },
        { label: '总部抖音账号', value: report.dou_yin_income },
        { label: '总部银行账号', value: report.transfer_income },
        { label: '总部小程序账号', value: report.applet_income },
      ];
    },
  },
  methods: {
    findPaymentModeName,
    tableRowClassName({ row }) {
      return row.is_refund ? 'warning-background' : '';
    },
  },
};
</script>

<style src="../report-card.css"></style>

<style scoped>
/* 明细表：去掉竖线只留横线，行更紧凑，宽屏下也不会像 Excel */
.report-sections /deep/ .el-table {
  font-size: 13px;
}

.report-sections /deep/ .el-table--border,
.report-sections /deep/ .el-table--border::after,
.report-sections /deep/ .el-table--border::before {
  border: 0;
}

.report-sections /deep/ .el-table--border th,
.report-sections /deep/ .el-table--border td {
  border-right: 0;
}

.report-sections /deep/ .el-table th.el-table__cell {
  background: #fafbfc;
  color: #606266;
  font-weight: 500;
}

.report-sections /deep/ .el-table th.el-table__cell,
.report-sections /deep/ .el-table td.el-table__cell {
  padding: 8px 0;
}

.report-sections /deep/ .el-table td.el-table__cell {
  border-bottom: 1px solid #f2f4f7;
}

.sub-title {
  margin: 20px 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.group-pagination {
  margin-top: 12px;
}

/* 表格里的「主信息 + 次要信息」两行排版：用纵向空间换横向宽度 */
.cell-main {
  line-height: 20px;
  color: #303133;
}

.cell-sub {
  line-height: 16px;
  font-size: 12px;
  color: #909399;
}

.group-extra {
  display: flex;
  align-items: baseline;
  margin-top: 12px;
  color: #606266;
  font-size: 13px;
}

.group-extra b {
  margin-left: 10px;
  font-variant-numeric: tabular-nums;
}

.pay-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 4px 24px;
}

.pay-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 10px 4px;
  border-bottom: 1px solid #f5f7fa;
  font-size: 13px;
  color: #606266;
}

.pay-item b {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

/* 0 就是没有：弱化成灰色常规字重，不参与强调 */
.pay-item b.is-zero {
  color: #c0c4cc;
  font-weight: 400;
}

.pay-item.is-total {
  color: #1f2d3d;
  font-weight: 600;
}

.pay-item.is-total b {
  font-weight: 600;
}
</style>
